import React, { useEffect, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { ToDesktopMark } from './ToDesktopMark';
import { HelpMenu } from './HelpMenu';
import { AccountMenu } from './AccountMenu';
import { CommandPalette } from './CommandPalette';
import { isCommandPaletteShortcut } from './commandSearch';
import type { NavTabItem } from './navModel';
import {
  CrumbSeparator,
  ExtrasSlot,
  HideOnMobile,
  Kbd,
  LogoLink,
  NavCluster,
  NavHeader,
  NavRow,
  NavSpacer,
  SearchButton,
  TabLink,
  TabsRow,
} from './NavStyles';

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export interface TopNavTabs {
  label: string;
  items: NavTabItem[];
  activeKey: string | null;
  /** Extra content rendered after a tab's label, keyed by tab key. */
  adornments?: Partial<Record<string, React.ReactNode>>;
}

interface TopNavProps {
  /** Breadcrumb-style switchers shown after the logo. */
  crumbs?: React.ReactNode;
  /** Live status, e.g. a running build. */
  status?: React.ReactNode;
  /** Contextual extras such as the setup checklist. */
  extras?: React.ReactNode;
  tabs?: TopNavTabs;
}

export const TopNav: React.FC<TopNavProps> = ({
  crumbs,
  status,
  extras,
  tabs,
}) => {
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isCommandPaletteShortcut(event)) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <NavHeader data-sticky-nav>
      <NavRow>
        <LogoLink to="/" aria-label="ToDesktop, all apps">
          <ToDesktopMark size={24} />
        </LogoLink>
        {crumbs && (
          <>
            <CrumbSeparator aria-hidden>/</CrumbSeparator>
            {crumbs}
          </>
        )}
        <NavSpacer />
        <NavCluster>
          {status}
          {extras && <ExtrasSlot>{extras}</ExtrasSlot>}
          <SearchButton
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Search"
            aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
          >
            <SearchOutlined style={{ fontSize: 15 }} />
            <span className="search-label">Search</span>
            <Kbd className="search-label">{isMac ? '⌘K' : 'Ctrl K'}</Kbd>
          </SearchButton>
          {/* On phones, docs and status are reachable from search. */}
          <HideOnMobile>
            <HelpMenu />
          </HideOnMobile>
          <AccountMenu />
        </NavCluster>
      </NavRow>
      {tabs && tabs.items.length > 0 && (
        <TabsRow aria-label={tabs.label}>
          {tabs.items.map((tab) => {
            const active = tab.key === tabs.activeKey;
            return (
              <TabLink
                key={tab.key}
                to={tab.to}
                $active={active}
                aria-current={active ? 'page' : undefined}
              >
                {tab.label}
                {tabs.adornments?.[tab.key]}
              </TabLink>
            );
          })}
        </TabsRow>
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </NavHeader>
  );
};
